'use client';

import { useTranslations } from 'next-intl';
import { CacheKeys } from '@headless-workspace/config';
import { AsyncData, ResultEmpty, ResultFailure, ResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, UserRecommendationsRepositoryType } from '../../../DI';
import { RecommendedProductsValue } from '../../../server';
import { RevalidationStrategy, useSwrResult } from '../hooks';
import { useAppLocale } from '../i18n';

const DEFAULT_ERROR_KEY = 'RECOMMENDED_PRODUCTS_ERROR';

type UseTargetedProductRecommendationsReturnType = AsyncData<RecommendedProductsValue | null>;

export const useTargetedProductRecommendations = (carouselId: string): UseTargetedProductRecommendationsReturnType => {
    const t = useTranslations('Discover.Home.carousel');
    const locale = useAppLocale();

    // We are using the useSwrResult hook here because we call a Headless API route
    // We do not need all the session revalidation and previous state data logics from useSafeSWR
    const { data, isLoading, error } = useSwrResult(
        CacheKeys.UserRecommendations,
        () => CommonDI.get(UserRecommendationsRepositoryType).getRecommendations({ locale, pageId: 'homepage' }),
        // Specific validation policy that will allow manual revalidation with mutate
        { ...RevalidationStrategy.ColdDataPolicy, revalidateOnMount: false },
    );

    if (!data) {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    DEFAULT_ERROR_KEY,
                    error instanceof Error ? error.message : t('error.productRecommendations'),
                ),
            };
        }

        return { isLoading, result: ResultEmpty() };
    }

    const recommendation = data.type === 'success' ? data.data.find((item) => item.id === carouselId) : undefined;

    return {
        isLoading,
        result: recommendation ? ResultSuccess(recommendation) : ResultEmpty(),
    };
};
